import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

// Contextos
import { useAuth } from '../../contexts/AuthContext';
import { useLoader } from '../../contexts/LoaderContext';
import { useToast } from '../../contexts/ToastContext';

// API
import { useResultadoTesteService } from '../../services/resultadoTesteServices';
import { useTurmaService } from '../../services/turmaServices';
import { useTesteService } from '../../services/testeServices';
import { useTrilhaService } from '../../services/trilhaServices';
import { useParticipanteService } from '../../services/participanteServices';
import { useParticipanteTrilhaStatusService } from '../../services/participanteTrilhaStatusServices';
import { URLS } from '../../constants/urls';

// Assets
import testePlaceHolder from '../../assets/cardBackgroundAlt.png';
import trilhaPlaceHolder from '../../assets/cardBackground.png';

// Estilos
import styles from './participantehomepage.module.css';

const DEBUG_TRILHAS_HOME = true;

function ParticipanteHomePage() {
    const navigate = useNavigate();

    const { usuario } = useAuth();
    const { showLoader, hideLoader } = useLoader();
    const { showToast } = useToast();
    const { listarResultadosPorParticipante, gerarPdfDevolutiva } = useResultadoTesteService();
    const { listarTurmasTestePendentes, listarParticipanteTurmasPorParticipante } = useParticipanteService();
    const { listarTrilhasStatusPorParticipanteTurma } = useParticipanteTrilhaStatusService();
    const { obterTurmaPorId } = useTurmaService();
    const { obterTestePorId } = useTesteService();
    const { obterTrilhaPorId } = useTrilhaService();

    const [dadosPorTurma, setDadosPorTurma] = useState({});
    const [pendencias, setPendencias] = useState([]);
    const carrosselRefs = useRef({});

    const imagemDeFundo = testePlaceHolder;
    const imagemDeFundoTrilha = trilhaPlaceHolder;

    // Link do WhatsApp
    const whatsappLink = "https://wa.me/+5511973723256";

    useEffect(() => {
        const carregarResultados = async () => {
            showLoader("Carregando resultados");
            if (DEBUG_TRILHAS_HOME) {
                console.log('[ParticipanteHomePage] carregarResultados:start', {
                    usuarioId: usuario?.id ?? null,
                });
            }
            const toBool = (value) => {
                if (typeof value === 'boolean') return value;
                if (typeof value === 'number') return value === 1;
                if (typeof value === 'string') {
                    const normalized = value.trim().toLowerCase();
                    return normalized === '1' || normalized === 'true' || normalized === 'sim';
                }
                return false;
            };
            const normalizePendenteItem = (item = {}) => {
                const idTurma = item?.idTurma ?? item?.id_turma ?? item?.turmaId ?? item?.turma_id ?? null;
                const idTeste = item?.idTeste ?? item?.id_teste ?? item?.testeId ?? item?.teste_id ?? null;
                const idTrilha = item?.idTrilha ?? item?.id_trilha ?? item?.trilhaId ?? item?.trilha_id ?? null;
                const possuiTrilha = toBool(item?.possuiTrilha ?? item?.possui_trilha ?? item?.temTrilha ?? item?.tem_trilha) || Boolean(idTrilha);
                const possuiTeste = toBool(item?.possuiTeste ?? item?.possui_teste ?? item?.temTeste ?? item?.tem_teste) || Boolean(idTeste);

                return {
                    ...item,
                    idTurma,
                    idTeste,
                    idTrilha,
                    possuiTrilha,
                    possuiTeste,
                };
            };
            const normalizeTrilhaStatusItem = (item = {}) => {
                const idTrilha = item?.idTrilha ?? item?.id_trilha ?? item?.trilhaId ?? item?.trilha_id ?? null;
                const percentualRaw = item?.percentual ?? item?.progress ?? item?.percent ?? item?.progresso ?? 0;
                const percentualSanitized = String(percentualRaw ?? '').replace(',', '.');
                const percentual = Number.isFinite(Number(percentualSanitized)) ? Number(percentualSanitized) : 0;
                const status = String(item?.status ?? item?.situacao ?? '').trim().toLowerCase();
                const concluida = status === 'concluida' || status === 'concluída' || percentual >= 100;
                const dataConclusao =
                    item?.dataConclusao ??
                    item?.data_conclusao ??
                    item?.updatedAt ??
                    item?.updated_at ??
                    item?.dataAtualizacao ??
                    item?.data_atualizacao ??
                    null;

                return {
                    ...item,
                    idTrilha,
                    percentual,
                    status,
                    concluida,
                    dataConclusao,
                };
            };
            const extractRows = (response) => {
                const direct = response?.data;
                if (Array.isArray(direct)) return direct;
                if (direct && typeof direct === 'object') {
                    if (Array.isArray(direct.data)) return direct.data;
                    if (Array.isArray(direct.rows)) return direct.rows;
                    if (Array.isArray(direct.items)) return direct.items;
                    return [direct];
                }
                return [];
            };
            const turmaCache = new Map();
            const testeCache = new Map();
            const trilhaCache = new Map();
            const getTurmaDetalhes = async (idTurma) => {
                if (!idTurma) return '';
                if (turmaCache.has(idTurma)) return turmaCache.get(idTurma);
                const resTurma = await obterTurmaPorId(idTurma);
                const data = resTurma?.success ? (resTurma?.data || {}) : {};
                const normalized = {
                    nome: data?.nome || `Turma ${idTurma}`,
                    idTrilha: data?.idTrilha ?? data?.id_trilha ?? null,
                };
                turmaCache.set(idTurma, normalized);
                return normalized;
            };
            const getTurmaNome = async (idTurma) => {
                const turma = await getTurmaDetalhes(idTurma);
                return turma?.nome || `Turma ${idTurma}`;
            };
            const getTesteNome = async (idTeste) => {
                if (!idTeste) return '';
                if (testeCache.has(idTeste)) return testeCache.get(idTeste);
                const resTeste = await obterTestePorId(idTeste);
                const nome = resTeste?.success ? (resTeste.data?.nome || 'Teste sem nome') : 'Teste sem nome';
                testeCache.set(idTeste, nome);
                return nome;
            };
            const getTrilhaNome = async (idTrilha) => {
                if (!idTrilha) return '';
                if (trilhaCache.has(idTrilha)) return trilhaCache.get(idTrilha);
                const resTrilha = await obterTrilhaPorId(idTrilha);
                const nome = resTrilha?.success ? (resTrilha.data?.titulo || resTrilha.data?.nome || 'Trilha sem titulo') : 'Trilha sem titulo';
                trilhaCache.set(idTrilha, nome);
                return nome;
            };

            const [res, pendentesRes] = await Promise.all([
                listarResultadosPorParticipante(usuario.id),
                listarTurmasTestePendentes({ idParticipante: usuario.id }),
            ]);
            if (DEBUG_TRILHAS_HOME) {
                console.log('[ParticipanteHomePage] respostas iniciais', {
                    resultadosSuccess: res?.success,
                    resultadosQtd: Array.isArray(res?.resultados) ? res.resultados.length : null,
                    pendentesSuccess: pendentesRes?.success,
                    pendentesQtd: Array.isArray(pendentesRes?.data) ? pendentesRes.data.length : null,
                    pendentesRaw: pendentesRes,
                });
            }

            hideLoader();
            console.log(res.resultados);
            const agrupadoResultados = {};
            if (res.success && res.resultados) {
                for (const r of res.resultados) {
                    const turmaNome = r.nomeTurma || `Turma ${r.idTurma}`;
                    if (!agrupadoResultados[turmaNome]) agrupadoResultados[turmaNome] = [];
                    agrupadoResultados[turmaNome].push({
                        ...r,
                        _tipo: 'teste_resultado',
                    });
                }
            } else {
                showToast("Erro ao carregar resultados.", 3000, 'error');
            }

            let pendenciasDetalhadas = [];
            if (pendentesRes?.success && Array.isArray(pendentesRes.data)) {
                pendenciasDetalhadas = await Promise.all(
                    pendentesRes.data.map(async (rawItem) => {
                        const item = normalizePendenteItem(rawItem);
                        const nomeTurma = await getTurmaNome(item.idTurma);
                        let nomeVinculo = '';
                        let tipoVinculo = '';

                        if (item.possuiTrilha && item.idTrilha) {
                            tipoVinculo = 'Trilha';
                            nomeVinculo = await getTrilhaNome(item.idTrilha);
                        } else if (item.possuiTeste && item.idTeste) {
                            tipoVinculo = 'Teste';
                            nomeVinculo = await getTesteNome(item.idTeste);
                        }

                        return {
                            ...item,
                            nomeTurma,
                            nomeVinculo,
                            tipoVinculo,
                        };
                    })
                );

            } else if (pendentesRes && pendentesRes.success === false) {
                showToast("Erro ao carregar pendencias.", 3000, 'error');
            }

            const participanteTurmasRes = await listarParticipanteTurmasPorParticipante(usuario.id);
            const participanteTurmasRows = extractRows(participanteTurmasRes);
            if (DEBUG_TRILHAS_HOME) {
                console.log('[ParticipanteHomePage] participanteTurmas catalogo', {
                    success: participanteTurmasRes?.success,
                    totalRows: participanteTurmasRows.length,
                    participanteTurmasResRaw: participanteTurmasRes,
                });
            }
            const turmasComTrilhaDoParticipante = (
                await Promise.all(
                    participanteTurmasRows.map(async (vinculo) => {
                        try {
                            const idTurma = String(vinculo?.idTurma ?? vinculo?.id_turma ?? '');
                            if (!idTurma) return null;
                            const turmaDetalhes = await getTurmaDetalhes(idTurma);

                            const idParticipanteTurma =
                                vinculo?.id ??
                                vinculo?.idParticipanteTurma ??
                                vinculo?.id_participante_turma ??
                                null;
                            const idTrilhaTurma =
                                turmaDetalhes?.idTrilha ??
                                vinculo?.idTrilha ??
                                vinculo?.id_trilha ??
                                null;

                            if (!idParticipanteTurma) return null;
                            if (!idTrilhaTurma) return null;

                            return {
                                idTurma,
                                idParticipanteTurma,
                                idTrilhaTurma,
                                nomeTurma: turmaDetalhes?.nome || `Turma ${idTurma}`,
                            };
                        } catch (error) {
                            console.warn('[ParticipanteHomePage] Falha no pre-filtro de turma com trilha:', {
                                idTurma: vinculo?.idTurma ?? vinculo?.id_turma ?? null,
                                vinculo,
                                error,
                            });
                            return null;
                        }
                    })
                )
            ).filter(Boolean);
            if (DEBUG_TRILHAS_HOME) {
                console.log('[ParticipanteHomePage] turmas com trilha e vínculo do participante', turmasComTrilhaDoParticipante);
            }

            const trilhasConcluidasPorTurmaList = await Promise.all(
                turmasComTrilhaDoParticipante.map(async (turmaCtx) => {
                    try {
                        const { idTurma, idParticipanteTurma, idTrilhaTurma, nomeTurma } = turmaCtx;
                        const trilhaStatusRes = await listarTrilhasStatusPorParticipanteTurma(idParticipanteTurma);
                        if (DEBUG_TRILHAS_HOME) {
                            console.log('[ParticipanteHomePage] trilhaStatus response', {
                                idTurma,
                                idParticipanteTurma,
                                success: trilhaStatusRes?.success,
                                trilhaStatusResRaw: trilhaStatusRes,
                            });
                        }
                        if (!trilhaStatusRes?.success) return [];

                        const statusRows = extractRows(trilhaStatusRes);
                        if (DEBUG_TRILHAS_HOME) {
                            console.log('[ParticipanteHomePage] statusRows', {
                                idTurma,
                                idParticipanteTurma,
                                count: statusRows.length,
                                statusRows,
                            });
                        }
                        if (!statusRows.length) return [];

                        const concluidas = await Promise.all(
                            statusRows.map(async (rawStatus) => {
                                const status = normalizeTrilhaStatusItem(rawStatus);
                                const idTrilha = status.idTrilha || idTrilhaTurma;
                                if (DEBUG_TRILHAS_HOME) {
                                    console.log('[ParticipanteHomePage] status item', {
                                        idTurma,
                                        rawStatus,
                                        statusNormalizado: status,
                                        idTrilhaFinal: idTrilha,
                                        idTrilhaTurma,
                                    });
                                }
                                if (!status.concluida || !idTrilha) return null;
                                const nomeTrilha = await getTrilhaNome(idTrilha);

                                return {
                                    idTurma,
                                    idTrilha,
                                    nomeTurma,
                                    nomeTrilha,
                                    dataConclusao: status.dataConclusao,
                                    percentual: status.percentual,
                                    status: status.status,
                                };
                            })
                        );
                        if (DEBUG_TRILHAS_HOME) {
                            console.log('[ParticipanteHomePage] concluidas turma', {
                                idTurma,
                                nomeTurma,
                                concluidas,
                            });
                        }

                        return concluidas.filter(Boolean);
                    } catch (error) {
                        console.warn('[ParticipanteHomePage] Falha ao carregar trilhas concluídas da turma:', turmaCtx?.idTurma, error);
                        return [];
                    }
                })
            );

            const trilhasConcluidasFlat = trilhasConcluidasPorTurmaList.flat();
            const trilhasConcluidasDedupe = Array.from(
                trilhasConcluidasFlat.reduce((acc, item) => {
                    const key = `${item.idTurma}-${item.idTrilha}`;
                    const current = acc.get(key);
                    if (!current || item.percentual >= current.percentual) {
                        acc.set(key, item);
                    }
                    return acc;
                }, new Map()).values()
            );
            if (DEBUG_TRILHAS_HOME) {
                console.log('[ParticipanteHomePage] trilhas flatten/dedupe', {
                    flatQtd: trilhasConcluidasFlat.length,
                    dedupeQtd: trilhasConcluidasDedupe.length,
                    trilhasConcluidasFlat,
                    trilhasConcluidasDedupe,
                });
            }

            const trilhasPendentesPorTurmaList = await Promise.all(
                turmasComTrilhaDoParticipante.map(async (turmaCtx) => {
                    try {
                        const { idTurma, idParticipanteTurma, idTrilhaTurma, nomeTurma } = turmaCtx;
                        const trilhaStatusRes = await listarTrilhasStatusPorParticipanteTurma(idParticipanteTurma);
                        if (!trilhaStatusRes?.success) return [];

                        const statusRows = extractRows(trilhaStatusRes);
                        const rowsToProcess = statusRows.length ? statusRows : [{ status: 'nao_iniciada', percentual: 0 }];

                        const pendentes = await Promise.all(
                            rowsToProcess.map(async (rawStatus) => {
                                const status = normalizeTrilhaStatusItem(rawStatus);
                                const idTrilha = status.idTrilha || idTrilhaTurma;
                                if (!idTrilha || status.concluida) return null;
                                const nomeTrilha = await getTrilhaNome(idTrilha);

                                return {
                                    idTurma,
                                    idTrilha,
                                    nomeTurma,
                                    nomeVinculo: nomeTrilha,
                                    tipoVinculo: 'Trilha',
                                    possuiTrilha: true,
                                    possuiTeste: false,
                                };
                            })
                        );
                        return pendentes.filter(Boolean);
                    } catch {
                        return [];
                    }
                })
            );
            const trilhasPendentesDedupe = Array.from(
                trilhasPendentesPorTurmaList.flat().reduce((acc, item) => {
                    const key = `${item.idTurma}-${item.idTrilha}`;
                    acc.set(key, item);
                    return acc;
                }, new Map()).values()
            );

            const pendenciasDedupe = Array.from(
                [...pendenciasDetalhadas, ...trilhasPendentesDedupe].reduce((acc, item) => {
                    const key = item?.possuiTrilha
                        ? `trilha-${item.idTurma}-${item.idTrilha ?? ''}`
                        : `teste-${item.idTurma}-${item.idTeste ?? ''}`;
                    acc.set(key, item);
                    return acc;
                }, new Map()).values()
            );
            setPendencias(pendenciasDedupe);

            trilhasConcluidasDedupe.forEach((item) => {
                const turmaNome = item.nomeTurma || `Turma ${item.idTurma}`;
                if (!agrupadoResultados[turmaNome]) agrupadoResultados[turmaNome] = [];
                agrupadoResultados[turmaNome].push({
                    ...item,
                    _tipo: 'trilha_concluida',
                });
            });
            setDadosPorTurma(agrupadoResultados);
            if (DEBUG_TRILHAS_HOME) {
                console.log('[ParticipanteHomePage] trilhas agrupadas no bloco de resultados', agrupadoResultados);
            }
        };

        if (usuario?.id) carregarResultados();
    }, [usuario?.id]);

    useEffect(() => {
        Object.values(carrosselRefs.current).forEach(carrossel => {
            if (!carrossel) return;

            let isDown = false;
            let startX;
            let scrollLeft;

            const onMouseDown = (e) => {
                isDown = true;
                startX = e.pageX - carrossel.offsetLeft;
                scrollLeft = carrossel.scrollLeft;
                carrossel.classList.add(styles.activeDrag);
            };

            const onMouseLeave = () => {
                isDown = false;
                carrossel.classList.remove(styles.activeDrag);
            };

            const onMouseUp = () => {
                isDown = false;
                carrossel.classList.remove(styles.activeDrag);
            };

            const onMouseMove = (e) => {
                if (!isDown) return;
                e.preventDefault();
                const x = e.pageX - carrossel.offsetLeft;
                const walk = (x - startX) * 1.5;
                carrossel.scrollLeft = scrollLeft - walk;
            };

            carrossel.addEventListener('mousedown', onMouseDown);
            carrossel.addEventListener('mouseleave', onMouseLeave);
            carrossel.addEventListener('mouseup', onMouseUp);
            carrossel.addEventListener('mousemove', onMouseMove);

            // Cleanup
            return () => {
                carrossel.removeEventListener('mousedown', onMouseDown);
                carrossel.removeEventListener('mouseleave', onMouseLeave);
                carrossel.removeEventListener('mouseup', onMouseUp);
                carrossel.removeEventListener('mousemove', onMouseMove);
            };
        });
    }, [dadosPorTurma]);

    const handleVerRespostas = async (resultado) => {
        let deveFazerFallback = true;

        const pdfWin = window.open('', '_blank');

        if (pdfWin) {
            try { pdfWin.opener = null; } catch (e) {}
            try {
                pdfWin.document.title = 'Gerando PDF...';
                pdfWin.document.body.style.fontFamily = 'sans-serif';
                pdfWin.document.body.style.padding = '16px';
                pdfWin.document.body.innerHTML = '<p>Gerando seu PDF, aguarde...</p>';
            } catch (e) {}
        }

        try {
            showLoader('Gerando PDF de devolutiva...');

            const resp = await gerarPdfDevolutiva(resultado); // aceita objeto com .id

            console.log('[ParticipanteHomePage.handleVerRespostas] resp:', resp);

            if (resp?.success && resp?.relative_path) {
                const relative = resp.relative_path; // ex: "generated/resultado_....pdf"

                // Base: URLS.IMG_PATH + /reports
                const imgBase = URLS.IMG_PATH || '';
                const baseSemBarraFinal = imgBase.endsWith('/')
                    ? imgBase.slice(0, -1)
                    : imgBase;

                const reportsBase = `${baseSemBarraFinal}/reports`;

                // Garante que não tenha duas barras
                const relativeSemBarraInicial = relative.startsWith('/')
                    ? relative.slice(1)
                    : relative;

                const pdfUrl = `${reportsBase}/${relativeSemBarraInicial}`;
   
                if (pdfWin && !pdfWin.closed) {
                    try {
                        pdfWin.location.replace(pdfUrl);
                    } catch (e) {
                        try { pdfWin.close(); } catch (e2) {}
                    }

                    setTimeout(() => {
                        try {
                            if (!pdfWin.closed && pdfWin.location.href === 'about:blank') {
                                pdfWin.close();
                            }
                        } catch (e) {
                            // Sem acesso => provavelmente navegou para o PDF (cross-origin). Tudo certo.
                        }
                    }, 8000);
                }

                deveFazerFallback = false;
            }
        } catch (err) {
            console.error('[ParticipanteHomePage.handleVerRespostas] erro ao gerar PDF:', err);
        } finally {
            hideLoader();
        }

        if (deveFazerFallback) {
            // Fecha a aba extra (se abriu) quando ocorrer fallback
            if (pdfWin && !pdfWin.closed) {
                try { pdfWin.close(); } catch (e) {}
            }
            navigate(`/participante/page/resultado/${resultado.id}`);
        }
    };

    const handleResponderPendente = (item) => {
        const destino = item.possuiTrilha
            ? `https://santeskills.com/ead/${item.idTurma}`
            : `${URLS.QR_CODE_PATH || ''}res/${item.idTurma}`;
        window.location.href = destino;
    };
    const handleAbrirTrilhaConcluida = (item) => {
        const destino = `https://santeskills.com/ead/${item.idTurma}`;
        window.location.href = destino;
    };
    const formatarDataConclusao = (rawDate) => {
        if (!rawDate) return 'Trilha concluida';
        const parsed = new Date(rawDate);
        if (Number.isNaN(parsed.getTime())) return 'Trilha concluida';
        return `Concluida em ${parsed.toLocaleDateString()}`;
    };

    return (
        <div className={styles.pageWrapper}>
            <div className={styles.containerTop}>
                <h1 className={styles.title}>Meus Resultados</h1>
            </div>

            {pendencias.length > 0 && (
                <div className={styles.turmaWrapper}>
                    <h2 className={styles.turmaTitulo}>Turmas pendentes</h2>

                    <div className={styles.pendingGrid}>
                        {pendencias.map((item) => (
                            <div
                                key={`${item.idTurma}-${item.idTeste || 't'}-${item.idTrilha || 'l'}`}
                                className={styles.pendingCard}
                            >
                                <div className={styles.pendingCardContent}>
                                    <div className={styles.pendingHeader}>
                                        <h3 className={styles.pendingTitle}>
                                            {item.nomeTurma || `Turma ${item.idTurma}`}
                                        </h3>
                                        {item.possuiTeste && <span className={styles.pendingBadgeTeste}>Teste</span>}
                                        {item.possuiTrilha && <span className={styles.pendingBadgeTrilha}>Trilha</span>}
                                    </div>
                                    <div className={styles.pendingSubtitle}>
                                        {item.nomeVinculo ? `${item.tipoVinculo}: ${item.nomeVinculo}` : 'Atividade pendente'}
                                    </div>
                                    {/* IDs omitidos */}
                                    <button
                                        className={styles.cardBotao}
                                        onClick={() => handleResponderPendente(item)}
                                    >
                                        Responder
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {Object.entries(dadosPorTurma).map(([turma, resultados]) => (
                <div key={turma} className={styles.turmaWrapper}>
                    <h2 className={styles.turmaTitulo}>{turma}</h2>

                    <div
                        className={styles.carrossel}
                        ref={(el) => (carrosselRefs.current[turma] = el)}
                    >
                        {resultados.map((item, i) => {
                            const isTrilhaConcluida = item?._tipo === 'trilha_concluida';
                            return (
                                <div
                                    key={`${item?._tipo || 'teste'}-${item?.id || item?.idTrilha || i}`}
                                    className={`${styles.card} ${isTrilhaConcluida ? styles.cardTrilha : styles.cardTeste}`}
                                    style={{
                                        backgroundImage: `url(${isTrilhaConcluida ? imagemDeFundoTrilha : imagemDeFundo})`,
                                    }}
                                >
                                    <div className={styles.cardContent}>
                                        <div className={styles.cardTypeRow}>
                                            <span className={isTrilhaConcluida ? styles.cardBadgeTrilha : styles.cardBadgeTeste}>
                                                {isTrilhaConcluida ? 'Trilha concluída' : 'Teste respondido'}
                                            </span>
                                        </div>
                                        <h3 className={styles.cardTitulo}>
                                            {isTrilhaConcluida ? (item.nomeTrilha || 'Trilha') : item.nomeTeste}
                                        </h3>

                                        <p className={styles.cardData}>
                                            {isTrilhaConcluida
                                                ? formatarDataConclusao(item.dataConclusao)
                                                : `Respondido em ${new Date(item.dataEnvio).toLocaleDateString()}`}
                                        </p>

                                        <button
                                            className={`${styles.cardBotao} ${isTrilhaConcluida ? styles.cardBotaoTrilha : styles.cardBotaoTeste}`}
                                            onClick={() => (isTrilhaConcluida ? handleAbrirTrilhaConcluida(item) : handleVerRespostas(item))}
                                        >
                                            {isTrilhaConcluida ? 'Acessar Trilha' : 'Ver Respostas'}
                                        </button>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            ))}
            
            <a
                href={whatsappLink}
                className={styles.whatsappButton}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Fale conosco no WhatsApp"
            >
                <img
                    src="https://upload.wikimedia.org/wikipedia/commons/6/6b/WhatsApp.svg"
                    alt="WhatsApp"
                    className={styles.whatsappIcon}
                />
            </a>
        </div>
    );
}

export default ParticipanteHomePage;


